import React, { useState, useEffect } from 'react';
import { Modal } from '@common/Modal';
import { Database, RefreshCw, Cloud, CloudOff, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@common/Button';
import { collection, query, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { useConnectionStatus } from '@hooks/useConnectionStatus';
import { syncPendingData } from '@utils/offlineStorage';
import { Toast } from '@common/Toast';

export const DatabaseStatusModal = ({ isOpen, onClose }) => {
  const { status, pendingUploads } = useConnectionStatus();
  const [syncing, setSyncing] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPendingItems = async () => {
      if (!isOpen) return;
      
      try {
        const pendingRef = collection(db, 'pendingUploads');
        const snapshot = await getDocs(pendingRef);
        setPendingItems(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      } catch (error) {
        console.error('Failed to load pending items:', error);
        setToast({
          type: 'error',
          message: 'Failed to load pending items'
        });
      }
    };

    loadPendingItems();
  }, [isOpen]);

  const handleForceSync = async () => {
    if (!navigator.onLine) {
      setToast({
        type: 'error',
        message: 'Cannot sync while offline'
      });
      return;
    }
    
    setSyncing(true);
    try {
      await syncPendingData();
      // Refresh pending items
      const pendingRef = collection(db, 'pendingUploads');
      const snapshot = await getDocs(pendingRef);
      setPendingItems(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      setToast({
        type: 'success',
        message: 'Data synced successfully'
      });
    } catch (error) {
      console.error('Failed to sync:', error);
      setToast({
        type: 'error',
        message: 'Failed to sync data'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleClearPending = async () => {
    if (!window.confirm('Are you sure you want to delete all pending items? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const pendingRef = collection(db, 'pendingUploads');
      const snapshot = await getDocs(pendingRef);
      
      await Promise.all(
        snapshot.docs.map(doc => deleteDoc(doc.ref))
      );

      setPendingItems([]);
      setToast({
        type: 'success',
        message: 'All pending items cleared'
      });
    } catch (error) {
      console.error('Failed to clear pending items:', error);
      setToast({
        type: 'error',
        message: 'Failed to clear pending items'
      });
    } finally {
      setLoading(false);
    }
  };

  const getItemDetails = (item) => {
    if (!item?.data) return 'Unknown';
    
    const details = [];
    if (item.data.type) details.push(`Type: ${item.data.type}`);
    if (item.data.action) details.push(`Action: ${item.data.action}`);
    if (item.data.id) details.push(`ID: ${item.data.id}`);
    
    return details.join(' • ') || 'No details available';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Database Status">
      <div className="p-6 space-y-6">
        {/* Connection Status */}
        <div className="bg-gray-700/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            {status === 'online' ? (
              <Cloud className="w-5 h-5 text-green-400" />
            ) : (
              <CloudOff className="w-5 h-5 text-red-400" />
            )}
            <h3 className="font-medium text-white">Connection Status</h3>
          </div>
          <p className="text-white capitalize">{status}</p>
        </div>

        {/* Pending Items */}
        <div className="bg-gray-700/50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              <h3 className="font-medium text-white">Pending Items</h3>
            </div>
            <span className="text-2xl font-bold text-white">{pendingItems.length}</span>
          </div>

          {pendingItems.length > 0 ? (
            <div className="space-y-2 mt-4">
              {pendingItems.map(item => (
                <div key={item.id} className="bg-gray-800/50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-blue-400 font-medium">{item.collection}</span>
                      <div className="text-sm text-gray-400">
                        {getItemDetails(item)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(item.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400">
                      Pending
                    </div>
                  </div>
                </div>
              ))}

              {/* Clear All Button */}
              <div className="mt-4 pt-4 border-t border-gray-600">
                <Button
                  onClick={handleClearPending}
                  disabled={loading}
                  className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear All Pending Items
                </Button>
                <div className="mt-2 flex items-start gap-2 text-xs text-yellow-400 bg-yellow-500/10 p-2 rounded-lg">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>Warning: This will permanently delete all pending uploads. This action cannot be undone.</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-4">
              No pending items
            </div>
          )}
        </div>

        {/* Sync Button */}
        <Button
          onClick={handleForceSync}
          disabled={!navigator.onLine || syncing}
          className="w-full"
        >
          <div className="flex items-center justify-center gap-2">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Force Sync'}
          </div>
        </Button>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};